// Which membership price new members get, and the numbers shown for it.
//
// Admins set this in Firestore at options/membershipPricing (Firebase console):
//   activePriceId  Stripe price id new members check out with (a price of the
//                  product marked role "member"), e.g. "price_1Abc..."
//   amount         price shown on the join page and sales page, e.g. 84
//   was            crossed-out "regular" price, e.g. 150 (leave out to hide it)
//   currency       e.g. "CAD"
//   period         e.g. "quarter"
//
// Existing subscribers are never affected: Stripe keeps billing each
// subscription at the price it started with.
// The sales page (sales-pages/src/components/momentum) reads the same doc.

export const MEMBERSHIP_PRICING_DOC = ["options", "membershipPricing"];

export const DEFAULT_MEMBERSHIP_PRICING = {
  activePriceId: null,
  amount: 84,
  was: 150,
  currency: "CAD",
  period: "quarter",
};

function toAmount(value) {
  const number = typeof value === "string" ? Number(value.replace(/[$,\s]/g, "")) : value;
  return typeof number === "number" && Number.isFinite(number) && number > 0 ? number : null;
}

function toText(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function formatPriceAmount(amount) {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

// Turns whatever is in the Firestore doc into safe display values. Anything
// missing or invalid falls back to today's numbers.
export function normalizeMembershipPricing(data) {
  const source = data && typeof data === "object" ? data : {};
  const amount = toAmount(source.amount) ?? DEFAULT_MEMBERSHIP_PRICING.amount;
  const hasWas = Object.prototype.hasOwnProperty.call(source, "was") || source.amount != null;
  const wasValue = hasWas ? toAmount(source.was) : DEFAULT_MEMBERSHIP_PRICING.was;
  const was = wasValue && wasValue > amount ? wasValue : null;

  return {
    activePriceId: toText(source.activePriceId),
    amount,
    was,
    discount: was ? `${Math.round((1 - amount / was) * 100)}%` : null,
    currency: toText(source.currency) || DEFAULT_MEMBERSHIP_PRICING.currency,
    period: toText(source.period) || DEFAULT_MEMBERSHIP_PRICING.period,
  };
}

// Picks the price new members check out with: the admin's chosen price when it
// is an active price of the member product, otherwise the first active price, otherwise
// the first price (the old behaviour).
export function pickMembershipPrice(prices, activePriceId) {
  if (!Array.isArray(prices) || prices.length === 0) return null;
  if (activePriceId) {
    const chosen = prices.find((price) => price.id === activePriceId);
    if (chosen && chosen.active !== false) return chosen;
    console.warn(`options/membershipPricing.activePriceId ${activePriceId} is not an active member price; using the default.`);
  }
  return prices.find((price) => price.active !== false) || prices[0];
}
